import React from "react";
import { BrowserRouter, Link, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { AbarytDateProvider } from "./context/AbarytDateContext";
import { CartProvider } from "./context/CartContext";
import CommunicationsHome from "./pages/CommunicationsHome";
import NationCommunications from "./pages/NationCommunications";
import NationStudio from "./pages/NationStudio";
import PublicLive from "./pages/PublicLive";
import CommunicationsRecordings from "./pages/CommunicationsRecordings";
import YasharalLiveSignup from "./pages/YasharalLiveSignup";
import AccountLogin from "./pages/AccountLogin";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import AccountSettings from "./pages/AccountSettings";
import AutoLogout from "./components/AutoLogout";
import { useAuth } from "./context/AuthContext";
import LivePublicHome from "./pages/LivePublicHome";
import "./App.css";
import "./lib/http";

// Standalone Live route shell. It intentionally contains no government-site header,
// favicon, logo or brand palette. The deploy entry point and branding must be
// configured separately before publishing on live.yasharal.vip.
// Legacy meeting links are preserved so existing invitations can be reused.
function LiveLoginRedirect() {
  React.useEffect(() => {
    const ret = encodeURIComponent(window.location.origin + "/yasharal-live");
    window.location.replace("https://gov.yasharal.vip/yasharal-live/login?next=%2Fyasharal-live&return_to=" + ret + "#features");
  }, []);
  return <div className="min-h-screen bg-slate-950 p-10 text-white">Opening account sign in…</div>;
}

function StudioGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-950 p-10 text-center text-white">Checking your account…</div>;
  if (!user) return (
    <div className="min-h-screen bg-slate-950 px-5 py-20 text-center text-white">
      <h1 className="text-2xl font-semibold">Sign in to your Live workspace</h1>
      <p className="mx-auto mt-3 max-w-lg text-slate-300">The studio needs an active Yashar’al account session. Sign in through the main account portal and return here.</p>
      <a className="mt-7 inline-flex rounded-xl bg-sky-500 px-6 py-3 font-semibold text-slate-950" href="/yasharal-live/login">Sign In</a>
      <div className="mt-5"><Link className="text-sky-300 underline" to="/">Back to public Live home</Link></div>
    </div>
  );
  return <React.Fragment key={String(user.id)}>{children}</React.Fragment>;
}

function LiveRoutes() {
  return (
    <div className="min-h-screen flex flex-col">
      <AutoLogout />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<LivePublicHome />} />
          <Route path="/live" element={<PublicLive />} />
          <Route path="/yasharal-live" element={<StudioGate><CommunicationsHome /></StudioGate>} />
          <Route path="/yasharal-live/login" element={<LiveLoginRedirect />} />
          <Route path="/yasharal-live/signup" element={<YasharalLiveSignup />} />
          <Route path="/yasharal-live/rooms/:roomId" element={<NationCommunications />} />
          <Route path="/yasharal-live/guest/:roomId" element={<NationCommunications />} />
          <Route path="/yasharal-live/studio/:roomId" element={<StudioGate><NationStudio /></StudioGate>} />
          <Route path="/yasharal-live/recordings" element={<StudioGate><CommunicationsRecordings /></StudioGate>} />
          <Route path="/communications" element={<StudioGate><CommunicationsHome /></StudioGate>} />
          <Route path="/communications/rooms/:roomId" element={<NationCommunications />} />
          <Route path="/communications/guest/:roomId" element={<NationCommunications />} />
          <Route path="/communications/studio/:roomId" element={<StudioGate><NationStudio /></StudioGate>} />
          <Route path="/communications/recordings" element={<StudioGate><CommunicationsRecordings /></StudioGate>} />
          <Route path="/login" element={<LiveLoginRedirect />} />
          <Route path="/portal" element={<Navigate to="/" replace />} />
          <Route path="/nationals/portal" element={<Navigate to="/" replace />} />
          <Route path="/nationals/login" element={<Navigate to="/" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/account/settings" element={<StudioGate><AccountSettings /></StudioGate>} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="*" element={<div className="p-8 text-center"><p>Page not found.</p><Link to="/" className="underline">Return to Live</Link></div>} />
        </Routes>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default function LiveApp() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AbarytDateProvider>
          <CartProvider>
            <LiveRoutes />
          </CartProvider>
        </AbarytDateProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
